<template>
  <div class="layer-picker">
    <!-- LAUNCHER -->
    <div v-if="!showPanel" class="launcher" @click="$emit('update:showPanel', true)">
      <img :src="currentType.img" :alt="currentType.name" />
      <span class="label">{{ currentType.name }}</span>
    </div>

    <!-- PANOU -->
    <div v-else class="map-control">
      <div class="mc-header">
        <span class="mc-title">Types</span>
        <button class="mc-btn" @click="$emit('update:showPanel', false)" aria-label="Close">
          <img :src="closeIcon" alt="" />
        </button>
      </div>

      <div class="mc-scroll">
        <!-- Types -->
        <div class="grid grid--3">
          <button
            v-for="type in typesToShow"
            :key="type.name"
            :class="['map-item', {active: selectedType === type.name}]"
            type="button"
            @click="$emit('selectType', type.name)"
          >
            <img :src="type.img" :alt="type.name" />
            <span>{{ type.name }}</span>
          </button>
        </div>

        <!-- Toggle Overlays / Weather -->
        <div class="toggles">
          <button class="toggle-btn" :class="{open: overlaysIsOpen}" type="button" @click="toggleOverlays">
            Overlays <span class="caret">{{ overlaysIsOpen ? "▲" : "▼" }}</span>
          </button>

          <button class="toggle-btn" :class="{open: weatherIsOpen}" type="button" @click="toggleWeather">
            Weather <span class="caret">{{ weatherIsOpen ? "▲" : "▼" }}</span>
          </button>
        </div>

        <!-- Sections -->
        <div class="sections">
          <transition name="collapse">
            <div class="section" v-show="overlaysIsOpen">
              <div class="grid grid--3">
                <button
                  v-for="overlay in overlaysToShow"
                  :key="overlay.name"
                  :class="['map-item', {active: activeOverlays && activeOverlays.includes(overlay.name)}]"
                  type="button"
                  @click="$emit('toggleOverlay', overlay.name)"
                >
                  <img :src="overlay.img" :alt="overlay.name" />
                  <span>{{ overlay.name }}</span>
                </button>
              </div>
            </div>
          </transition>

          <transition name="collapse">
            <div class="section" v-show="weatherIsOpen">
              <div class="grid grid--3">
                <button
                  v-for="w in weatherToShow"
                  :key="w.name"
                  :class="['map-item', {active: activeOverlays && activeOverlays.includes(w.name)}]"
                  type="button"
                  @click="$emit('toggleOverlay', w.name)"
                >
                  <img :src="w.img" :alt="w.name" />
                  <span>{{ w.name }}</span>
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// TYPES
import terrain from "@/assets/thumbs/terrain.png";
import dark from "@/assets/thumbs/dark.png";
import winter from "@/assets/thumbs/winter.png";
import closeIcon from "@/assets/icons/close.png";

// WEATHER
import windIcon from "@/assets/thumbs/wind.png";
import precipitationIcon from "@/assets/thumbs/precipitation.png";
import temperatureIcon from "@/assets/thumbs/temperature.png";

// OVERLAYS
import skiingIcon from "@/assets/thumbs/skiing.png";
import cyclingIcon from "@/assets/thumbs/cycling.png";
import protectedIcon from "@/assets/thumbs/protected.png";
import hikingIcon from "@/assets/thumbs/hiking.png";

export default {
  name: "ControlerLayersMTK",
  data() {
    return {
      closeIcon
    };
  },
  props: {
    showPanel: {type: Boolean, default: false},
    selectedType: {type: String, default: "Terrain"},
    activeOverlays: {type: Array, default: () => []},
    overlaysOpen: {type: Boolean, default: false},
    weatherOpen: {type: Boolean, default: false},

    types: {
      type: Array,
      default: () => [
        {name: "Terrain", img: terrain},
        {name: "Dark", img: dark},
        {name: "Winter", img: winter}
      ]
    },
    overlays: {
      type: Array,
      default: () => [
        {name: "Hiking", img: hikingIcon},
        {name: "Cycling", img: cyclingIcon},
        {name: "Skiing", img: skiingIcon},
        {name: "Protected", img: protectedIcon}
      ]
    },
    weatherLayers: {
      type: Array,
      default: () => [
        {name: "Temperature", img: temperatureIcon},
        {name: "Precipitation", img: precipitationIcon},
        {name: "Wind", img: windIcon}
      ]
    }
  },
  watch: {
    showPanel(val) {
      if (val) {
        this.overlaysIsOpen = false; // emit 'update:overlaysOpen'
        this.weatherIsOpen = false; // emit 'update:weatherOpen'
      }
    }
  },
  computed: {
    typesToShow() {
      return this.types;
    },
    overlaysToShow() {
      return this.overlays;
    },
    weatherToShow() {
      return this.weatherLayers;
    },
    currentType() {
      const t = (this.types || []).find(t => t.name === this.selectedType);
      return t || (this.types && this.types[0]) || {name: "", img: ""};
    },
    // bridge pt. .sync
    overlaysIsOpen: {
      get() {
        return this.overlaysOpen;
      },
      set(v) {
        this.$emit("update:overlaysOpen", v);
      }
    },
    weatherIsOpen: {
      get() {
        return this.weatherOpen;
      },
      set(v) {
        this.$emit("update:weatherOpen", v);
      }
    }
  },
  methods: {
    goBack() {
      this.$emit("update:showPanel", false);
    },
    toggleOverlays() {
      const next = !this.overlaysIsOpen;
      this.overlaysIsOpen = next;
      if (next) this.weatherIsOpen = false;
    },
    toggleWeather() {
      const next = !this.weatherIsOpen;
      this.weatherIsOpen = next;
      if (next) this.overlaysIsOpen = false;
    }
  }
};
</script>

<style scoped>
.layer-picker {
  display: none;
  position: absolute;
  top: 70px;
  right: 10px;
  z-index: 5000;
}
.launcher {
  padding: 5px;
  width: 95px;
  height: 90px;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 2px 4px 4px #999999;
  cursor: pointer;
  position: relative;
  background: #fff;
  border: 1px solid #a9a9a9;
}
.launcher img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #a9a9a9;
}
.launcher .label {
  position: absolute;
  top: 6px;
  left: 6px;
  padding: 2px 8px;
  border-radius: 5px;
  font-family: Quicksand, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #464646;
  line-height: 1.2em;
  letter-spacing: 0.03em;
  flex: 1;
  background: #ecf0f3;
}

.map-control {
  background: #ecf0f3;
  padding: 10px 12px 12px;
  border-radius: 14px;
  width: 320px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  user-select: none;
  max-height: 320px;
  border: 1px solid #a9a9a9;
}
.mc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.mc-title {
  font-family: Quicksand, sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #464646;
  line-height: 1.2em;
  letter-spacing: 0.03em;
  flex: 1;
}
.mc-btn {
  border: none;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  cursor: pointer;
  line-height: 26px;
  text-align: center;
  font-size: 16px;
}
.mc-btn:hover {
  width: 25px;
  height: 25px;
}

.grid {
  display: grid;
  gap: 8px;
  margin-bottom: 8px;
}
.grid--3 {
  grid-template-columns: repeat(3, 1fr);
}

.toggles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin: 6px 0 8px;
}
.toggle-btn {
  padding: 6px 10px;
  border-radius: 10px;
  font-weight: 400;
  cursor: pointer;
  text-align: center;
  font-family: Quicksand, sans-serif;
  font-size: 14px;
  color: #464646;
  line-height: 1.2em;
  letter-spacing: 0.03em;
  flex: 1;
}
.toggle-btn.open {
  font-weight: 700;
}
.toggle-btn .caret {
  margin-left: 6px;
  font-weight: 700;
}

.sections {
  display: block;
}
.section {
  width: 100%;
}

.map-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 2px solid transparent;
  border-radius: 12px;
  /* padding: 4px;  */
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, transform 0.05s;
  font-family: Quicksand, sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #464646;
  line-height: 1.2em;
  letter-spacing: 0.03em;
  flex: 1;
}

.map-item:active {
  transform: translateY(1px);
}
.map-item.active {
  border-color: #8e97a5;
}
.map-item img {
  width: 100%;
  height: 70px;
  object-fit: cover;
  border-radius: 10px;
}
.map-item span {
  display: block;
  font-size: 13px;
  margin-top: 4px;
}

.collapse-enter-active,
.collapse-leave-active {
  transition: max-height 0.2s ease, opacity 0.2s ease;
  overflow: hidden;
}
.collapse-enter,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}
.collapse-enter-to,
.collapse-leave {
  max-height: 500px;
  opacity: 1;
}

.map-control {
  background: #ecf0f3;
  border: 1px solid #a9a9a9;
  padding: 10px 12px 12px;
  border-radius: 14px;
  width: 320px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  user-select: none;

  max-height: min(70vh, 420px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.mc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  position: sticky;
  top: 0;
  z-index: 2;
}

.mc-scroll {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-right: 1px;
}

.toggles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 7px;
  margin: 6px 0 8px;
  position: sticky;
  top: 42px;
  z-index: 1;
  padding-top: 4px;
}

.grid {
  display: grid;
  gap: 8px;
  margin-bottom: 8px;
}
.grid--3 {
  grid-template-columns: repeat(3, 1fr);
}
.sections {
  display: block;
}
.section {
  width: 100%;
}
.collapse-enter-active,
.collapse-leave-active {
  transition: max-height 0.2s ease, opacity 0.2s ease;
  overflow: hidden;
}
.collapse-enter,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}
.collapse-enter-to,
.collapse-leave {
  max-height: 500px;
  opacity: 1;
}
</style>
